import { useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useSuspectDays, useResolveSuspectDay } from '../../api/revenue';
import type { SuspectDay, SuspectResolution } from '../../api/revenue';
import { useMyPermissions } from '../../api/queries';
import { useAlert, useConfirm } from '../../components/dialogs/DialogProvider';
import { fmtCurrencyCents, fmtDate, fmtOrEmDash } from '../../utils/format';
import { SuspectDayDetailDialog, reasonLabel } from './SuspectDayDetailDialog';

/**
 * "Suspect meter-day review" dialog. Opened from the Revenue dashboard's
 * provisional/flagged banner. Lists the suspect meter-days in the current
 * scope + period with their money movement so a reviewer can inspect, then —
 * if they hold `meters.audit-ack` — resolve each one:
 *   • Confirm good    → clears the suspect flag;
 *   • Confirm suspect → keeps it flagged but records who/when reviewed it.
 *
 * NO editing of meter VALUES here (a future "amend values" flow). Review state
 * is written in place on the meter-day row; on success the dialog list AND the
 * dashboard's flagged count / red trend markers refetch (the resolve mutation
 * invalidates the ['revenue', ...] query tree).
 *
 * Only rendered while `open` (the caller guards it) so the data hooks never run
 * on a closed dialog.
 */
export function SuspectDaysDialog({
  open, onClose, period, from, to, siteId, machineId, title = 'Suspect meter-days',
}: {
  open: boolean;
  onClose: () => void;
  period: string;
  from?: string;
  to?: string;
  siteId?: string | null;
  machineId?: string | null;
  title?: string;
}) {
  const q = useSuspectDays(period, from, to, { siteId, machineId, enabled: open });
  const resolve = useResolveSuspectDay();
  const perms = useMyPermissions();
  const canAck = !!perms.data?.codes.includes('meters.audit-ack');
  const confirm = useConfirm();
  const alert = useAlert();
  const [detailDay, setDetailDay] = useState<SuspectDay | null>(null);

  const rows = q.data ?? [];

  async function onResolve(day: SuspectDay, resolution: SuspectResolution) {
    const ok = await confirm(
      resolution === 'good'
        ? {
            title: 'Confirm meters good',
            message: `Confirm ${day.machineLabel} on ${fmtDate(day.gamingDay)} is good? This clears the suspect flag.`,
            confirmLabel: 'Confirm good',
          }
        : {
            title: 'Keep flagged as suspect',
            message: `Keep ${day.machineLabel} on ${fmtDate(day.gamingDay)} flagged as suspect? It stays flagged but is marked reviewed.`,
            confirmLabel: 'Confirm suspect',
          },
    );
    if (!ok) return;
    try {
      await resolve.mutateAsync({ id: day.id, resolution });
    } catch (e) {
      await alert({
        title: 'Could not resolve',
        message: (e as { message?: string })?.message ?? 'Failed to resolve the suspect meter-day.',
        severity: 'error',
      });
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {q.isLoading && (
          <Typography variant="body2" color="text.secondary">Loading suspect meter-days…</Typography>
        )}
        {q.isError && (
          <Alert severity="error">
            {(q.error as { message?: string })?.message ?? 'Failed to load suspect meter-days.'}
          </Alert>
        )}
        {!q.isLoading && !q.isError && rows.length === 0 && (
          <Alert severity="info">No suspect meter-days in this scope and period.</Alert>
        )}
        {rows.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="suspect meter-days" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Machine</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Gaming day</TableCell>
                  <TableCell>Game</TableCell>
                  <TableCell align="right">Turnover</TableCell>
                  <TableCell align="right">Out</TableCell>
                  <TableCell align="right">Jackpots</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Review state</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {rows.map((d) => (
                  // Whole row opens the meter detail (owner request: with ten
                  // nowrap columns the Actions cell can sit off-screen; the row
                  // itself must be the affordance).
                  <TableRow key={d.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailDay(d)}>
                    <TableCell>{fmtOrEmDash(d.machineLabel)}</TableCell>
                    <TableCell>{fmtOrEmDash(d.siteName)}</TableCell>
                    <TableCell>{fmtDate(d.gamingDay)}</TableCell>
                    <TableCell>{gameLabel(d)}</TableCell>
                    <TableCell align="right">{fmtCurrencyCents(d.turnoverCents)}</TableCell>
                    <TableCell align="right">{fmtCurrencyCents(d.totalOutCents)}</TableCell>
                    <TableCell align="right">{fmtCurrencyCents(d.jackpotsCents)}</TableCell>
                    <TableCell><ReasonChip day={d} /></TableCell>
                    <TableCell><ReviewStateChip day={d} /></TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small" variant="text"
                          onClick={() => setDetailDay(d)}
                        >
                          Detail
                        </Button>
                        {canAck && (
                          <>
                            <Button
                              size="small" color="success" variant="outlined"
                              disabled={resolve.isPending}
                              onClick={() => onResolve(d, 'good')}
                            >
                              Confirm good
                            </Button>
                            <Button
                              size="small" color="warning" variant="outlined"
                              disabled={resolve.isPending}
                              onClick={() => onResolve(d, 'suspect')}
                            >
                              Confirm suspect
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {rows.length > 0 && !canAck && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            You have view-only access. Resolving a suspect meter-day requires the meter-audit acknowledge permission.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {detailDay !== null && (
        <SuspectDayDetailDialog
          open
          onClose={() => setDetailDay(null)}
          day={detailDay}
          allowResolve
        />
      )}
    </Dialog>
  );
}

function ReasonChip({ day }: { day: SuspectDay }) {
  if (!day.suspectReason) {
    return (
      <Typography variant="body2" color="text.secondary" component="span">
        {fmtOrEmDash(null)}
      </Typography>
    );
  }
  return <Chip size="small" color="warning" variant="outlined" label={reasonLabel(day.suspectReason)} />;
}

function gameLabel(d: SuspectDay): string {
  const denom = d.denominationCents != null ? fmtCurrencyCents(d.denominationCents) : null;
  if (d.gameName && denom) return `${d.gameName} · ${denom}`;
  if (d.gameName) return d.gameName;
  if (denom) return denom;
  return fmtOrEmDash(null);
}

function ReviewStateChip({ day }: { day: SuspectDay }) {
  if (!day.suspectReviewedAt) {
    return <Chip size="small" color="warning" label="Needs review" />;
  }
  const who = day.suspectReviewedByName;
  return <Chip size="small" color="default" label={who ? `Reviewed by ${who}` : 'Reviewed'} />;
}
